import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { dashboardRefreshIntervalInMs } from "~/areas/dashboard/DashboardOverview/DashboardOverviewPage";
import type { RenderDashboardProps } from "~/areas/dashboard/DashboardOverview/DashboardOverviewPage";
import type { DataCube } from "~/areas/projects/components/DashboardDataSource/DataCube";
import ProgressionDataCube from "~/areas/projects/components/DashboardDataSource/ProgressionDataCube";
import { repository } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { useRefreshLoop } from "~/hooks/useRefreshLoop";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import { useEphemeralEnvironmentsEnabled } from "../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { getLiveStatus } from "./liveStatusLoader";
interface UntenantedProjectDashboardDataSourceProps {
    project: ProjectResource;
    render: (props: RenderDashboardProps) => JSX.Element;
    doBusyTask: DoBusyTask;
    cube: DataCube | undefined;
    onCubeLoaded: (cube: DataCube, totalItems: number) => void;
    showLiveStatus: boolean;
}
export default function UntenantedProjectDashboardDataSource({ doBusyTask, project, render, cube, onCubeLoaded, showLiveStatus }: UntenantedProjectDashboardDataSourceProps) {
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const dashboardRequest = useDoBusyTaskEffect(doBusyTask, async () => {
        const cube = await projectDashboardRefresh(project, isGrpcHaEnabled, ephemeralEnvironmentsEnabled);
        onCubeLoaded(cube, cube.data.Releases.length);
    }, [project], { timeOperationOptions: timeOperationOptions.forRefresh() });
    useRefreshLoop(dashboardRequest, dashboardRefreshIntervalInMs);
    return render({ cube, showLiveStatus });
}
async function projectDashboardRefresh(project: ProjectResource, isGrpcHaEnabled: boolean, ephemeralEnvironmentsEnabled: boolean): Promise<ProgressionDataCube> {
    const projectLiveStatusPromise = getLiveStatus(repository, project.Id, isGrpcHaEnabled);
    const dataPromise = repository.Progression.getProgression(project);
    const allChannelsPromise = repository.Channels.allFromProject(project);
    const deploymentFreezesPromise = repository.DeploymentFreezes.activeByProject(project);
    const data = await dataPromise;
    const allChannels = (await allChannelsPromise).filter((channel) => !ephemeralEnvironmentsEnabled || channel.Type === "Lifecycle");
    const deploymentFreezes = await deploymentFreezesPromise;
    const projectLiveStatusResponse = await projectLiveStatusPromise;
    const progressionData = new ProgressionDataCube(data, project, deploymentFreezes.DeploymentFreezes, [projectLiveStatusResponse.ProjectLiveStatus]);
    progressionData.addAdditionalChannels(allChannels);
    return progressionData;
}
